/**
 * De zijpanelen van de Chats-pagina, één op één met de oude bundle:
 * Live Feed, Waiting queue, Spenders, Unanswered messages, Private chats,
 * New followers, Timewasters, Unlocks & Tips en New Subscriptions.
 *
 * 18-10-2026 (Jordy): multi-chatter inbox. Unanswered zit nu IN de Waiting
 * queue (groen bovenaan); op zijn plek staat "My chats" (de eigen box).
 * New followers is weg: het nieuwe Mass Message-systeem regelt dat.
 *
 * Inhoud, kolommen, badges en helpteksten zijn overgenomen zoals ze in het
 * oude scherm staan; alleen de opmaak is die van de nieuwe stijl. De vier
 * wachtrij-panelen worden client-side verdeeld — zie `lib/panels.ts`.
 */
import { InfoKnop } from './InfoKnop'
import { BAK_LABEL, feedIndeling, SOORT_ICOON, type FeedIndeling } from '../lib/feedSoort'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Plat, Tag, Wacht } from './ui'
import { dtSec, dtShort, eur } from '../lib/format'
import { bestedingAlleModellen, claimActief, isGroen, isWaiting } from '../lib/panels'
import { NieuweAbonneeTag } from './NieuweAbonneeTag'
import { klokFase, restSeconden } from '../lib/claimKlok'
import { feedKey, groepeerBundels } from '../lib/liveFeed'
import {
  ergsteUrgentie, oudste, urgentieVan, wachttijdKort,
} from '../lib/urgentie'
import type { QueueRow } from '../lib/panels'
import { massVoortgang } from '../lib/massVoortgang'
import { ONLINE_VERS_MIN } from '../lib/volgersFilter'

/**
 * Het tijdstip waarop de wachttijd van een rij telt: het bericht van de fan
 * dat nog onbeantwoord is, en anders zijn laatste bericht.
 */
const stempel = (c: QueueRow): unknown =>
  c.queue_fan_message_at || c.last_fan_message_at

/** Paneel met vaste hoogte en eigen scroll, net als de oude kolompanelen. */
export function SidePanel({
  title,
  count,
  desc,
  right,
  children,
  prio,
  prioKleur,
  urgentie,
  oudsteMin,
  onScrollEnd,
  tour,
  online,
  filterBalk,
}: {
  title: string
  count?: number
  /** 2-11-2026 (Jordy, opdracht E): achter het totaal "· n online"; null = onbekend
   *  (geen online-signaal), undefined = niet tonen. */
  online?: number | null
  /** 2-11-2026: de melding over een actief filter, boven de lijst. */
  filterBalk?: ReactNode
  desc?: ReactNode
  right?: ReactNode
  children: ReactNode
  /** Volgnummer van het blok op de pagina (26-10-2026: 1–8). */
  prio?: number
  /** 26-10-2026: kleur van het nummerblokje (p1–p6); standaard het nummer zelf.
   *  Zo houdt elk blok zijn eigen kleur nu de nummers verschoven zijn. */
  prioKleur?: number
  /** De zwaarste urgentie in dit paneel; kleurt de teller. */
  urgentie?: 'ok' | 'warn' | 'bad'
  /** Hoe lang de langst wachtende rij al staat, in minuten. */
  oudsteMin?: number | null
  /** Aangeroepen als de lijst (bijna) tot onderaan is gescrold. */
  onScrollEnd?: () => void
  /** 18-10-2026: anker voor de intro-tour (lib/tour.ts), als data-tour. */
  tour?: string
}) {
  return (
    <div className="panel fixed" data-tour={tour}>
      <div className="head">
        <div>
          <h3>
            {prio ? <span className={`prio p${prioKleur ?? prio}`}>{prio}</span> : null}
            {title}
            {count === undefined ? null : (
              <span className={`koptel ${urgentie ?? 'ok'}`}>
                {' '}({count}
                {oudsteMin != null ? ` · oldest ${wachttijdKort(oudsteMin)}` : ''})
              </span>
            )}
            {online === undefined ? null : (
              <span className="koptel online" data-testid="kop-online"
                    title="Fans the platform reported online in the last 10 minutes">
                {' '}· {online === null ? 'online unknown' : `${online} online`}
              </span>
            )}
            {/* 30-10-2026 (Jordy): de uitleg achter een i-knopje, niet meer als
                regel onder de titel (zie components/InfoKnop.tsx). */}
            {desc ? <InfoKnop label={`About ${title}`}>{desc}</InfoKnop> : null}
          </h3>
        </div>
        {right ? <div className="r">{right}</div> : null}
      </div>
      {filterBalk ?? null}
      <div className="body flush">
        <div
          className="panelscroll"
          onScroll={onScrollEnd ? (e) => {
            const el = e.currentTarget
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) onScrollEnd()
          } : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * Elke regel in een paneel opent het gesprek, net als in het oude scherm.
 * Zonder dit kon een chatter alleen vanuit de grote tabel een chat openen en
 * niet vanuit de wachtrij, de Live Feed of de privé-lijst — precies de
 * plekken waar hij werkt.
 */
/**
 * Een chat-rij als ECHTE link, zodat de browser rechtermuisknop "openen in
 * nieuw tabblad/venster", middenklik en ctrl/cmd-klik zelf aanbiedt — net als
 * oud. Een gewone linkerklik blijft SPA-navigatie: react-router's `Link`
 * onderschept alleen de ongemodificeerde klik en laat de rest native gaan, dus
 * geen dubbele navigatie en geen herlaadbeurt. Rijen zonder geldig
 * conversation_id blijven een gewone (niet-klikbare) rij.
 */
function RijLink({ id, extra, vanaf, children }: {
  id: unknown
  extra?: string
  /** 02-10: `waiting` — de chat meldt zich bij openen als geopend vanuit de wachtrij. */
  vanaf?: string
  children: ReactNode
}) {
  const n = Number(id)
  if (!Number.isFinite(n) || n <= 0) {
    return <div className={`feedrow${extra ? ` ${extra}` : ''}`}>{children}</div>
  }
  return (
    <Link to={`/conversations/show/${n}${vanaf ? `?from=${vanaf}` : ''}`}
          className={`feedrow openrow${extra ? ` ${extra}` : ''}`}>
      {children}
    </Link>
  )
}

function EmptyRow({ children }: { children: ReactNode }) {
  return <div className="feedrow mut2" style={{ justifyContent: 'center' }}>{children}</div>
}

const fanName = (c: Record<string, unknown>): string =>
  String(c.fan_display_name || c.fan_username || c.fan_id || '—')

/**
 * Wie het laatste bericht stuurde, zoals oud het op een wachtrij-regel zet:
 * een fan heet "Fan", de AI "Automated", een chatter draagt zijn eigen naam
 * en valt terug op "Chatter", een onbekende afzender heet "Unknown".
 *
 * New followers gebruikt bewust een ándere terugval — zie `senderLabel`
 * verderop — omdat de welkomstberichten daar als `human` zonder chatter in de
 * data staan.
 */
function rowSender(c: QueueRow): string {
  // 20-10-2026: bij een nieuwe volger is het laatste bericht het welkomstbericht
  // van het platform (of een Mass DM), nooit een persoonlijk antwoord.
  if (c.nieuwe_volger) return 'Automated'
  switch (String(c.last_message_sender ?? '').toLowerCase()) {
    case 'fan': return 'Fan'
    case 'human': return String(c.last_message_chatter_name ?? '').trim() || 'Chatter'
    case 'ai': return 'Automated'
    default: return 'Unknown'
  }
}

/** De kleur van het afzenderlabel volgt oud: fan grijs, AI paars, chatter groen. */
function senderKind(label: string): 'mut' | 'acc' | 'ok' {
  if (label === 'Fan' || label === 'Unknown') return 'mut'
  if (label === 'Automated') return 'acc'
  return 'ok'
}

/**
 * De bedragbadge op een chatregel. Oud kleurt op hoogte: vanaf €150 groen,
 * vanaf €50 oranje, daaronder rood — een kleine besteder valt zo juist op.
 */
function SpendTag({ amount }: { amount: number }) {
  const kind = amount >= 150 ? 'ok' : amount >= 50 ? 'warn' : 'bad'
  return <Tag kind={kind}>{eur(amount)}</Tag>
}

/**
 * "m:ss" tot een tijdstip, minstens 0:00; tikt elke seconde. 18-10-2026 (Jordy):
 * draagt de fase (groen > 3:00, oranje 1:00–3:00, rood < 1:00) als data-fase;
 * de hele rij licht daarop op via CSS (theme.css, :has()), zonder extra
 * renderwerk buiten dit ene klokje.
 */
function Aftellen({ tot }: { tot: unknown }) {
  const [, tik] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => tik((n) => n + 1), 1000)
    return () => window.clearInterval(t)
  }, [])
  const sec = restSeconden(tot)
  return (
    <span className="klokfase" data-fase={klokFase(sec)}>
      {Math.floor(sec / 60)}:{String(sec % 60).padStart(2, '0')}
    </span>
  )
}

/**
 * 18-10-2026 (Jordy): de inbox-badges van één rij.
 *  - groen "Unanswered": de fan wacht, geopend of net vrijgevallen, niemand
 *    heeft het nu (lib/panels.ts isGroen) — voor iedereen gelijk;
 *  - grijs "Being handled by X": iemand anders heeft het open (advies);
 *  - "Yours" (wacht op de fan) of "Yours · m:ss" (de fan antwoordde; 5 min);
 *    bij owner/admin staat de naam van de chatter die het gesprek heeft.
 */
export function InboxBadges({ c, deel = 'alles' }: {
  c: QueueRow
  /** 18-10-2026 (Jordy): in een paneelregel staat "Unanswered"/open-label achter de
   *  tekst ('tekst') en de claim ("Yours · tijd") na de €-badge ('claim'). */
  deel?: 'alles' | 'tekst' | 'claim'
}) {
  const tekst = deel !== 'claim'
  const claim = deel !== 'tekst'
  const ander = c.inbox_open_name && !c.inbox_open_mine && !claimActief(c)
  const wie = c.inbox_claim_mine ? 'Yours' : String(c.inbox_claim_name ?? 'Chatter')
  return (
    <>
      {tekst && isGroen(c) ? (
        <Tag kind="ok" title="Opened (or just released) and not answered yet — first priority">Unanswered</Tag>
      ) : null}
      {tekst && ander ? (
        <span className="openlabel" data-testid="open-label"
              title="Someone has this chat open. This does not block you; the chat becomes theirs only when they reply.">
          Being handled by {String(c.inbox_open_name)}
        </span>
      ) : null}
      {/* 21-10-2026 (Jordy): claim bij openen, nog geen antwoord. */}
      {claim && claimActief(c) && c.inbox_claim_state === 'open' ? (
        <Tag kind="acc" title="Opened and not answered yet: stays with this chatter until they reply or leave the chat">
          {wie} · open
        </Tag>
      ) : null}
      {claim && claimActief(c) && c.inbox_claim_state === 'waiting' ? (
        <Tag kind="acc" title="Waiting for the fan: stays with this chatter while they keep sending messages; lost at log-out or after more than an hour without sending a message">
          {wie}
        </Tag>
      ) : null}
      {claim && claimActief(c) && c.inbox_claim_state === 'replied' ? (
        <Tag kind="warn" title="The fan replied: 5 minutes to answer, then the chat leaves My chats and goes to the Waiting queue">
          {wie} · <Aftellen tot={c.inbox_claim_until} />
        </Tag>
      ) : null}
      {/* 20-10-2026 (Jordy): een vrijgevallen chat ("released": 5 min om; "expired":
          eigenaarschap kwijt) is van niemand meer en toont dus ook geen chatternaam. */}
    </>
  )
}

/**
 * Eén regel in een paneel, in dezelfde volgorde als het oude scherm:
 * platform, model, online-stip, fan, VIP, mislukte verzending, afzender +
 * bericht, de extra badges van dit paneel, het tijdstip en de wachttijd.
 */
function QueueLine({
  c,
  amount,
  avatarOf,
  extra,
  trailing,
  onClose,
  vanaf,
  moment,
  sluitTitel,
}: {
  c: QueueRow
  amount?: number | null
  avatarOf?: (slug: unknown) => string | undefined
  /** Badges die alleen dit paneel toont (bv. "Fan replied"). */
  extra?: ReactNode
  /** Wat helemaal rechts staat; standaard de wachttijd, zoals oud. */
  trailing?: ReactNode
  /** Sluitknop, alleen op de panelen waar oud er een heeft. */
  onClose?: () => void
  vanaf?: string
  /** 20-10-2026: de getoonde datum als die niet het fanbericht is (nieuwe volger: het welkomstbericht). */
  moment?: string | null
  sluitTitel?: string
}) {
  const stamp = c.queue_fan_message_at || c.last_fan_message_at
  // De rij draagt links een dunne rand in de kleur van de wachttijd; het
  // paneel zelf blijft rustig.
  const u = urgentieVan(stamp)
  const label = rowSender(c)
  const besteding = Number(c.fan_total_spend ?? 0)
  const bedrag = amount != null ? Number(amount) : besteding
  const mislukt = c.last_send_failed ? String(c.last_send_error ?? 'Send failed') : null
  return (
    <RijLink id={c.id} extra={`u-${u}`} vanaf={vanaf}>
      <Plat value={c.platform} />
      <span className="fmodel">
        <Avatar name={c.stage_name} src={avatarOf?.(c.model_slug)} size={18} />
        {String(c.stage_name ?? c.model_slug ?? '—')}
      </span>
      {c.fan_online === true || c.fan_online === false ? (
        <span
          className="dot"
          title={c.fan_online ? 'Online' : 'Offline'}
          style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                   background: c.fan_online ? 'var(--ok)' : 'var(--bad)' }}
        />
      ) : null}
      <span className="ffan">{fanName(c as Record<string, unknown>)}</span>
      {c.is_vip ? <Tag kind="acc">VIP</Tag> : null}
      {mislukt ? <Tag kind="bad">{sendError(mislukt)}</Tag> : null}
      <Tag kind={senderKind(label)}>{label}</Tag>
      <span className="ftext">{String(c.last_message ?? '')}</span>
      <InboxBadges c={c} deel="tekst" />
      {/* 18-10-2026 (Jordy): [€] [Yours · tijd] [datum]. */}
      {bedrag > 0 ? <SpendTag amount={bedrag} /> : null}
      {/* 19-10-2026 (Jordy): niets bij dit model, wel bij een ander: toch een spender.
          20-10-2026: als duidelijke "Spender"-markering (Jordy). */}
      {bedrag <= 0 && Number(c.fan_total_spend_all_models || 0) > 0 ? (
        <Tag kind="warn" title={`Spender: ${eur(Number(c.fan_total_spend_all_models))} spent with other models · €0 with this model`}>
          ★ Spender
        </Tag>
      ) : null}
      <InboxBadges c={c} deel="claim" />
      <NieuweAbonneeTag c={c} />
      {extra}
      <span className="mut2 ftime">{dtShort(moment ?? stamp)}</span>
      {trailing !== undefined ? trailing
        : <span className="fwait"><Wacht sinds={stamp} /></span>}
      {onClose ? (
        <button
          className="btn ghost sm"
          type="button"
          title={sluitTitel ?? 'Close this waiting message as spam or no action needed'}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose() }}
        >
          ×
        </button>
      ) : null}
    </RijLink>
  )
}

/**
 * De korte reden onder een mislukte verzending, met dezelfde woorden als oud —
 * een chatter moet in één blik zien of de fan weg is of ontvolgd heeft.
 */
/** Zelfde tekst als NEUTRALE_VERZENDFOUT in src/services/f2f_neutraal.py. */
export const F2F_NIET_VERZONDEN = 'Not sent. F2F is temporarily unavailable, try again later'

function sendError(err: string): string {
  const t = err.toLowerCase()
  if (t.includes('no chatuser matches') || t.includes('cannot find this fan')) return 'Fan gone'
  if (t.includes('unfollow') || t.includes("can't send messages in this chat")
      || t.includes('cannot send messages in this chat') || t.includes('chat is closed')) {
    return 'Unfollowed'
  }
  if (t.includes('not authenticated')) return 'Login needed'
  // 31-10-2026 (Jordy): andere rollen dan owner/admin krijgen van de server deze
  // tekst in plaats van de loginfout (src/services/f2f_neutraal.py).
  if (t.startsWith('not sent. f2f is temporarily unavailable')) return F2F_NIET_VERZONDEN
  // 18-10-2026 (Jordy): FC leverde een bijlage niet af. "fan is offline" was onze
  // eigen gok (oude rijen); de echte oorzaak was een tijdelijk uploadprobleem.
  if (t.includes("didn't deliver the attachment") || t.includes('because the fan is offline')) return 'Not delivered'
  return 'Send failed'
}

export function WaitingPanel({ rows, avatarOf }: {
  rows: QueueRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    // Oud zet hier bewust geen "Manage all"-knop; die hoort alleen bij de
    // privé-chats, waar je een hele groep tegelijk toewijst.
    <SidePanel
      tour="waiting-queue"
      title="Waiting queue"
      count={rows.length}
      prio={1}
      prioKleur={2}
      desc="Longest wait first – newest at the bottom. The green 'Unanswered' badge marks a chat that was opened or just released and still has no reply. A chat another chatter is handling right now is not shown."
      urgentie={ergsteUrgentie(rows.map(stempel))}
      oudsteMin={oudste(rows.map(stempel))}
    >
      {rows.length === 0 ? <EmptyRow>No fans waiting.</EmptyRow>
        : rows.map((c) => <QueueLine key={c.id} c={c} avatarOf={avatarOf} vanaf="waiting" />)}
    </SidePanel>
  )
}

/**
 * 2-11-2026 (Jordy, opdracht E): de melding als er in New followers of VIP een
 * filter aan staat ("Showing online only"), met een knop terug naar de hele lijst.
 * Het kopje erboven toont altijd het totaal.
 */
export function FilterBalk({ tekst, onReset, testid }: {
  tekst: string
  onReset: () => void
  testid: string
}) {
  return (
    <div className="filterbalk" data-testid={testid} role="status">
      <span className="tag warn">{tekst}</span>
      <button type="button" className="btn ghost sm" data-testid={`${testid}-reset`} onClick={onReset}>
        Show all
      </button>
    </div>
  )
}

/**
 * 26-10-2026 (Jordy): New followers als eigen blok (5). Een nieuwe volger blijft
 * staan tot een chatter een persoonlijk bericht stuurt (Mass DM's, het welkomstbericht
 * en systeemberichten tellen niet); schrijft de fan zelf, dan staat hij in Waiting.
 * Nieuwste volger bovenaan, het totaal in de kop, per 100 bijladen.
 * 28-10-2026 (Jordy): filters in de kop (`right`): Online only, Online first,
 * platform en model (lib/volgersFilter.ts). De regels hierboven blijven gelijk.
 */
export function NewFollowersPanel({ rows, total, avatarOf, onWeg, onMeer, dagen, right, leeg, online, filterBalk }: {
  rows: QueueRow[]
  /** Het totaal volgens de server (kan groter zijn dan wat er geladen is). 2-11-2026:
   *  altijd het totaal van het blok, ook als er een filter aan staat. */
  total: number
  /** 2-11-2026: "· n online" in de kop; null = onbekend. */
  online?: number | null
  /** 2-11-2026: de melding over een actief filter. */
  filterBalk?: ReactNode
  avatarOf?: (slug: unknown) => string | undefined
  /** Een nieuwe volger wegklikken (eigen marker, niets verstuurd). */
  onWeg?: (id: number) => void
  /** De volgende 100 laden; alleen als er meer zijn. */
  onMeer?: () => void
  /** Het venster in dagen (src/services/nieuwe_volgers.py VENSTER_DAGEN); 2-11-2026:
   *  standaard geen venster meer, een volger blijft tot een persoonlijk bericht. */
  dagen?: number
  /** 28-10-2026: de filters in de kop. */
  right?: ReactNode
  /** Tekst bij een lege lijst (met een filter aan is dat iets anders dan "geen"). */
  leeg?: ReactNode
}) {
  return (
    <SidePanel
      title="New followers"
      count={total}
      prio={5}
      online={online}
      filterBalk={filterBalk}
      desc={`Fans who started following${dagen ? ` in the last ${dagen} days` : ''} and never wrote. They stay here until a chatter sends a personal message — mass messages and the welcome message don't count; they don't drop off after a number of days. Newest first. Online = the platform reported the fan online in the last ${ONLINE_VERS_MIN} minutes; an older status counts as unknown.`}
      right={right}
      onScrollEnd={onMeer}
    >
      {rows.length === 0 ? <EmptyRow>{leeg ?? 'No new followers.'}</EmptyRow>
        : rows.map((c) => (
            <QueueLine key={`nv-${c.id}`} c={c} avatarOf={avatarOf} vanaf="waiting"
                       moment={c.nieuwe_volger_op ?? null} trailing={null}
                       extra={<Tag kind="acc" title="Started following; has not written and got no personal message yet">New follower</Tag>}
                       onClose={onWeg ? () => onWeg(c.id) : undefined}
                       sluitTitel="Remove this new follower from the list (nothing is sent)" />
          ))}
      {onMeer && rows.length < total ? (
        <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
          <button className="btn ghost sm" type="button" onClick={onMeer}>
            Load more ({rows.length} of {total})
          </button>
        </div>
      ) : null}
    </SidePanel>
  )
}

/** 24-10-2026 (Jordy): in Spenders kort het verschil, "€0,00 here · €107,65 elsewhere". */
function SpendHierElders({ c }: { c: QueueRow }) {
  const hier = Number(c.fan_total_spend || 0)
  const elders = Math.max(0, bestedingAlleModellen(c) - hier)
  return (
    <span className="mut2" data-testid="spend-hier-elders" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
          title="Spent with this model · spent with other models">
      {eur(hier)} here · {eur(elders)} elsewhere
    </span>
  )
}

export function SpendersPanel({ rows, avatarOf }: {
  rows: QueueRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    <SidePanel
      tour="spenders"
      title="Spenders"
      count={rows.length}
      prio={2}
      prioKleur={1}
      urgentie={ergsteUrgentie(rows.map(stempel))}
      oudsteMin={oudste(rows.map(stempel))}
    >
      {rows.length === 0 ? <EmptyRow>No spenders waiting.</EmptyRow>
        : rows.map((c) => (
            <QueueLine key={c.id} c={c} amount={c.fan_total_spend} avatarOf={avatarOf}
                       vanaf="waiting"
                       extra={<SpendHierElders c={c} />} />
          ))}
    </SidePanel>
  )
}

export function MyChatsPanel({ rows, avatarOf, onClose, allesZien, onRelease }: {
  rows: QueueRow[]
  avatarOf?: (slug: unknown) => string | undefined
  /** Sluit één wachtende melding, hetzelfde endpoint als oud. */
  onClose?: (id: number) => void
  /** Owner/admin: de claims van ALLE chatters, met een filter per chatter. */
  allesZien?: boolean
  /** 19-10-2026 (Jordy): alleen owner/admin. Opent de bevestiging van "Release chats":
   *  'alle' of het account-id van de gekozen chatter. */
  onRelease?: (doel: 'alle' | number) => void
}) {
  // 18-10-2026 (Jordy): de eigen box, op de plek van het oude "Unanswered
  // messages". Owner/admin zien hier alle lopende claims van alle chatters.
  const [chatter, setChatter] = useState('')
  const namen = useMemo(
    () => [...new Set(rows.map((c) => String(c.inbox_claim_name ?? '')).filter(Boolean))].sort(),
    [rows])
  const zichtbaar = allesZien && chatter ? rows.filter((c) => String(c.inbox_claim_name ?? '') === chatter) : rows
  const chatterId = chatter
    ? Number(rows.find((c) => String(c.inbox_claim_name ?? '') === chatter)?.inbox_claim_admin_id) || null
    : null
  return (
    <SidePanel
      tour="my-chats"
      title="My chats"
      count={zichtbaar.length}
      prio={3}
      urgentie={ergsteUrgentie(zichtbaar.filter((c) => isWaiting(c)).map(stempel))}
      oudsteMin={oudste(zichtbaar.filter((c) => isWaiting(c)).map(stempel))}
      desc={allesZien
        ? "All chats the chatters have right now, with who has them. A chat stays with a chatter while they keep sending messages; they lose it when they log out or go more than an hour without sending a message, and it returns to the Waiting queue as soon as the fan writes. Once the fan replies, the owner has 5 minutes to respond; after that the chat leaves My chats and goes to the Waiting queue."
        : "Chats you replied to. They stay yours while you keep sending messages; you lose them when you log out or go more than an hour without sending a message, and they return to the Waiting queue for everyone as soon as the fan writes. Once the fan replies, you have 5 minutes to respond; after that the chat leaves My chats and goes to the Waiting queue."}
      right={allesZien && namen.length ? (
        <span className="row" style={{ gap: 6, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
          <select className="select" value={chatter} aria-label="Filter by chatter" data-testid="mychats-filter"
                  onChange={(e) => setChatter(e.target.value)} style={{ maxWidth: 170 }}>
            <option value="">All chatters</option>
            {namen.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {onRelease && chatterId ? (
            <button className="btn ghost sm" type="button" data-testid="mychats-release-een" style={{ whiteSpace: 'nowrap' }}
                    onClick={() => onRelease(chatterId)}>
              Release {chatter}'s chats
            </button>
          ) : onRelease ? (
            <button className="btn ghost sm" type="button" data-testid="mychats-release-alle" style={{ whiteSpace: 'nowrap' }}
                    onClick={() => onRelease('alle')}>
              Release all
            </button>
          ) : null}
        </span>
      ) : undefined}
    >
      {zichtbaar.length === 0 ? <EmptyRow>{allesZien ? 'No chatter has a chat right now.' : 'No chats of yours right now.'}</EmptyRow>
        : zichtbaar.map((c) => (
            <QueueLine key={c.id} c={c} avatarOf={avatarOf}
                       // Geen wachttijd als de fan niet wacht (de chatter antwoordde het laatst).
                       trailing={isWaiting(c) ? undefined : null}
                       onClose={onClose && isWaiting(c) ? () => onClose(c.id) : undefined} />
          ))}
    </SidePanel>
  )
}

export function TimewastersPanel({ rows, avatarOf }: {
  rows: QueueRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    // 21-10-2026 (Jordy): was "Timewasters". Echte timewasters staan niet meer op
    // de hoofdpagina (alleen onder het All chats-filter); hier de "maybe".
    <SidePanel
      title="Maybe timewasters"
      count={rows.length}
      prio={8}
      prioKleur={6}
      desc="Fans marked as maybe timewaster who are waiting for a reply."
    >
      {rows.length === 0 ? <EmptyRow>No maybe timewasters waiting.</EmptyRow>
        : rows.map((c) => <QueueLine key={c.id} c={c} avatarOf={avatarOf} />)}
    </SidePanel>
  )
}

/**
 * 21-10-2026 (Jordy): wie NU online is binnen het merk, met dezelfde definitie als
 * het All chats-filter "Online now" (platformsignaal, fan_presence). Zonder
 * signaal zegt het paneel dat eerlijk in plaats van "niemand online".
 */
export function OnlinePanel({ rows, avatarOf, beschikbaar }: {
  rows: QueueRow[]
  avatarOf?: (slug: unknown) => string | undefined
  /** false = er is nu geen online-signaal van de platforms. */
  beschikbaar?: boolean | null
}) {
  // 21-10-2026 (Jordy): chats tellen per model; één fan kan met meerdere modellen
  // chatten. Daarom ook het aantal verschillende fans.
  const fans = new Set(rows.map((c) =>
    `${String(c.platform ?? '').toLowerCase()}:${String(c.fan_id ?? '').replace(/^@/, '').toLowerCase()}`)).size
  return (
    <SidePanel
      title="Online"
      count={rows.length}
      prio={7}
      prioKleur={5}
      desc={`Fans the platform reported online in the last 10 minutes · ${rows.length} chats from ${fans} fans. An older online status counts as unknown.`}
    >
      {rows.length === 0 ? (
        <EmptyRow>
          {beschikbaar === false
            ? 'Online status is not available right now (no live signal from the platforms yet).'
            : 'No fans online right now.'}
        </EmptyRow>
      ) : rows.map((c) => <QueueLine key={c.id} c={c} avatarOf={avatarOf} />)}
    </SidePanel>
  )
}

/* ------------------------------------------------------------------ private */

export interface PrivateRow extends QueueRow {
  stage_name?: string | null
  model_slug?: string | null
  claimed_by_name?: string | null
  last_message?: string | null
  last_message_chatter_name?: string | null
}

export function PrivatePanel({
  rows,
  total,
  modelLabel,
  avatarOf,
  onManageAll,
  platform,
}: {
  rows: PrivateRow[]
  total: number
  /** 06-10-2026: het gekozen model van de pagina, of leeg voor alle modellen. */
  modelLabel?: string
  avatarOf?: (slug: unknown) => string | undefined
  onManageAll?: () => void
  /** 'all' | 'fc' | 'f2f' — oud toont een chip zodra er gefilterd wordt. */
  platform?: string
}) {
  // Oud telt hoeveel fans hier op antwoord wachten en zet dat als groene
  // badge naast de kop.
  const wachtend = rows.filter((c) => isWaiting(c)).length
  return (
    <SidePanel
      tour="private"
      title="Private chats — all combined"
      count={total}
      prio={6}
      prioKleur={4}
      urgentie={ergsteUrgentie(rows.map(stempel))}
      oudsteMin={oudste(rows.map(stempel))}
      desc="Every private chat on the floor in your model scope, for the model and platform chosen above. Green badge = fan replied and waits."
      right={
        <div className="row" style={{ gap: 6 }}>
          {wachtend > 0 ? (
            <Tag kind="ok">{wachtend} fan {wachtend === 1 ? 'reply' : 'replies'}</Tag>
          ) : null}
          {platform && platform !== 'all' ? (
            <Tag kind="mut">{platform === 'f2f' ? 'F2F only' : 'FC only'}</Tag>
          ) : null}
          <Tag kind="mut">{modelLabel || 'All models'}</Tag>
          {onManageAll ? (
            <button className="btn sm" type="button" onClick={onManageAll}>Manage all</button>
          ) : null}
        </div>
      }
    >
      {rows.length === 0 ? (
        <EmptyRow>No chatter has been assigned a private chat yet.</EmptyRow>
      ) : rows.map((c) => (
        // Oud toont hier geen wachttijd achteraan (trailing is leeg) maar wel
        // wie de chat heeft: `claimed_by_name` draagt álle chatters met
        // toegang, komma-gescheiden.
        <QueueLine
          key={c.id}
          c={c}
          avatarOf={avatarOf}
          trailing={null}
          extra={
            <>
              {isWaiting(c) ? <Tag kind="ok">Fan replied</Tag> : null}
              {!c.claimed_by_me && c.claimed_by_name ? (
                <Tag kind="acc">{String(c.claimed_by_name)}</Tag>
              ) : null}
            </>
          }
        />
      ))}
    </SidePanel>
  )
}

/* ------------------------------------------------------------ new followers */

export interface FollowerRow {
  id: number
  platform: string
  model_slug: string
  stage_name: string | null
  fan_id: string
  fan_username: string | null
  fan_display_name: string | null
  last_message: string | null
  last_message_at: string | null
  last_message_sender: string | null
  total_messages: number
}

/**
 * "Fan" / "Automated" / de naam van de chatter — zoals `rut` in de bundle.
 *
 * Let op de terugval bij `human`: de welkomstberichten in New followers staan
 * als `human` in de data maar hebben geen chatter. Oud toont die als
 * "Automated", niet als "Chatter".
 */
export function senderLabel(sender: unknown, chatter?: unknown): string {
  switch (String(sender ?? '').toLowerCase()) {
    case 'fan': return 'Fan'
    case 'human': return String(chatter ?? '').trim() || 'Automated'
    case 'ai': return 'Automated'
    default: return 'Unknown'
  }
}

export function FollowersPanel({ rows, total, avatarOf }: {
  rows: FollowerRow[]
  total: number
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    <SidePanel
      prio={5}
      title="New followers"
      count={total}
      desc="Stays here until someone follows up or the fan writes back (last 14 days). Newer chats on other models do not push these out. Filter by model if the list is long. Not the same as the follower-count scoreboard."
    >
      {rows.length === 0 ? <EmptyRow>No new followers.</EmptyRow> : rows.map((c) => (
        <RijLink key={c.id} id={c.id}>
          <Plat value={c.platform} />
          <span className="fmodel">
            <Avatar name={c.stage_name} src={avatarOf?.(c.model_slug)} size={18} />
            {String(c.stage_name ?? c.model_slug ?? '—')}
          </span>
          <span className="ffan">{fanName(c as unknown as Record<string, unknown>)}</span>
          <Tag kind="mut">{senderLabel(c.last_message_sender)}</Tag>
          <span className="ftext">{String(c.last_message ?? '')}</span>
          <span className="mut2 ftime">{dtShort(c.last_message_at)}</span>
          <span className="mut2 fwait">{dtShort(c.last_message_at)}</span>
        </RijLink>
      ))}
    </SidePanel>
  )
}

/* --------------------------------------------------- unlocks & subscriptions */

export interface MoneyRow {
  id: number
  conversation_id?: number | null
  platform: string
  model: string | null
  model_slug: string | null
  fan: string | null
  fan_username: string | null
  fan_display_name: string | null
  amount: number | null
  currency: string | null
  event_type: string | null
  label?: string | null
  chatter_name?: string | null
  last_chatter_name?: string | null
  attribution_name?: string | null
  /** "Sent by" of "Last before tip" — de server bepaalt welke regel gold. */
  attribution_rule_label?: string | null
  created_at: string | null
}

/**
 * "Attachment unlock · Fan: … · Chatter: … · Sent by: …" zoals de oude regel.
 *
 * Twee verschillende dingen, die nieuw door elkaar haalde: `chatter_name` is
 * de chatter aan wie de verkoop is TOEGEWEZEN — leeg betekent "Unassigned" —
 * en `attribution_name` is wie het bericht daadwerkelijk stuurde. Het label
 * daarvoor komt van de server mee (`attribution_rule_label`: "Sent by" bij een
 * unlock, "Last before tip" bij een fooi). Nieuw viel voor de eerste terug op
 * de tweede en schreef "Chatter: Banki · Sent by: Banki", terwijl de verkoop
 * aan niemand is toegewezen.
 */
function unlockLine(r: MoneyRow): string {
  const kind = r.label || (r.event_type === 'tip' ? 'Tip' : 'Attachment unlock')
  const fan = r.fan_display_name || r.fan || r.fan_username || '—'
  const parts = [`${kind} · Fan: ${fan} · Chatter: ${r.chatter_name || 'Unassigned'}`]
  if (r.attribution_name) {
    const label = r.attribution_rule_label
      || (String(r.event_type) === 'tip' ? 'Last before tip' : 'Sent by')
    parts.push(`${label}: ${r.attribution_name}`)
  }
  return parts.join(' · ')
}

export function UnlocksPanel({ rows, avatarOf }: {
  rows: MoneyRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    <SidePanel title="Unlocks & Tips">
      {rows.length === 0 ? <EmptyRow>No unlocks or tips in this window.</EmptyRow> : rows.map((r) => (
        <RijLink key={`${r.event_type}-${r.id}`} id={r.conversation_id}>
          <Plat value={r.platform} />
          <span className="mut2 ftime">{dtShort(r.created_at)}</span>
          <span className="fmodel">
            <Avatar name={r.model} src={avatarOf?.(r.model_slug)} size={18} />
            {String(r.model ?? r.model_slug ?? '—')}
          </span>
          <span className="ftext">{unlockLine(r)}</span>
          <span className="num mono famt">{eur(r.amount)}</span>
        </RijLink>
      ))}
    </SidePanel>
  )
}

/**
 * 21-10-2026 (Jordy): VIP (was "New Subscriptions"; nieuwe subscripties staan nu
 * bij Spenders). Alle VIP-chats: handmatig VIP of de fan gaf iets uit. Blijvend:
 * ook als wij als laatst schreven. Wie wacht staat bovenaan (en óók gewoon in de
 * Waiting queue); dit paneel haalt niemand ergens weg.
 * 30-10-2026 (Jordy): dezelfde filters in de kop als New followers (`right`):
 * Online only, Online first, platform en model (lib/volgersFilter.ts), met een
 * eigen keuze. Wie VIP is en de volgorde blijven gelijk.
 */
export function VipPanel({ rows, total, avatarOf, right, leeg, online, filterBalk }: {
  rows: QueueRow[]
  /** 2-11-2026 (Jordy, opdracht E): het totaal van het blok, ook met een filter aan. */
  total?: number
  /** 2-11-2026: "· n online" in de kop; null = onbekend. */
  online?: number | null
  /** 2-11-2026: de melding over een actief filter. */
  filterBalk?: ReactNode
  avatarOf?: (slug: unknown) => string | undefined
  /** 30-10-2026: de filters in de kop. */
  right?: ReactNode
  /** Tekst bij een lege lijst (met een filter aan is dat iets anders dan "geen"). */
  leeg?: ReactNode
}) {
  const wachtend = rows.filter((c) => isWaiting(c))
  return (
    <SidePanel
      title="VIP"
      count={total ?? rows.length}
      prio={4}
      online={online}
      filterBalk={filterBalk}
      urgentie={ergsteUrgentie(wachtend.map(stempel))}
      desc={`All VIP fans: marked VIP or spent money. Fans waiting for a reply on top; the rest stays here too. Online = the platform reported the fan online in the last ${ONLINE_VERS_MIN} minutes; an older status counts as unknown.`}
      right={right}
    >
      {rows.length === 0 ? <EmptyRow>{leeg ?? 'No VIP fans yet.'}</EmptyRow>
        : rows.map((c) => (
          <QueueLine key={c.id} c={c} avatarOf={avatarOf}
                     extra={isWaiting(c) ? <Tag kind="ok">Fan replied</Tag> : null}
                     trailing={isWaiting(c) ? undefined : <span className="mut2 fwait">answered</span>} />
        ))}
    </SidePanel>
  )
}

export function SubscriptionsPanel({ rows, avatarOf }: {
  rows: MoneyRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  return (
    <SidePanel title="New Subscriptions">
      {rows.length === 0 ? <EmptyRow>No new subscriptions.</EmptyRow> : rows.map((r) => (
        <RijLink key={`sub-${r.id}`} id={r.conversation_id}>
          <Plat value={r.platform} />
          <span className="fmodel">
            <Avatar name={r.model} src={avatarOf?.(r.model_slug)} size={18} />
            {String(r.model ?? r.model_slug ?? '—')}
          </span>
          <span className="ffan">
            {String(r.fan_display_name || r.fan || r.fan_username || '—')}
          </span>
          <span className="mut2 ftime">{dtShort(r.created_at)}</span>
          <span className="num mono famt">{eur(r.amount)}</span>
        </RijLink>
      ))}
    </SidePanel>
  )
}

/* -------------------------------------------------------------- live feed */

export interface ActivityRow {
  id?: number
  type: string
  timestamp: string | null
  model: string | null
  model_slug: string | null
  platform: string | null
  message: string | null
  fan?: string | null
  chatter?: string | null
  conversation_id?: number | null
  inbox_kind?: string | null
  sender_identity?: string | null
  /** De naam van wie er stuurde; oud zet die op de badge. */
  sender_admin_name?: string | null
  mass_message_log_id?: number | null
  /** "native" betekent dat het platform zelf uitwaaierde. */
  mass_message_kind?: string | null
  /** 30-10-2026: "welcome" voor het automatische welkomstbericht (backend). */
  auto_kind?: string | null
  media_type?: string | null
  has_media?: boolean | null
  is_paid?: boolean | null
  price?: number | null
  collapsed_count?: number | null
  /** 20-10-2026: onderdelen van één bundelbericht (lib/liveFeed.ts groepeerBundels). */
  bundle_id?: string | null
  bundle_count?: number | null
}

/** De Live Feed toont ALLEEN berichten. `/dashboard/activity` levert ook
 *  post_published / post_due; die horen op de posting-pagina, niet hier. */
export const isMessageRow = (r: ActivityRow): boolean =>
  String(r.type ?? '').endsWith('_message')

/**
 * De badges achter elke regel, in de volgorde van de oude pagina:
 *   1. kanaal        "FC chat" / "F2F chat"
 *   2. afzender      "Fan" / "Dashboard · <chatter>" /
 *                    "FC · sent on platform" / "Platform Mass DM · <chatter>"
 *   3. postbus       "Shared inbox" / "Private · <chatter>"
 *   4. bijlage       "Photo" / "PPV photo · €20"
 */
/**
 * De badges op een regel in de Live Feed, in dezelfde volgorde en met dezelfde
 * woorden als het oude scherm.
 *
 * Drie stuks: waar de chat hoort (FC/F2F chat), wie er stuurde, en uit welke
 * bak de chat komt. Die derde is een rechttoe rechtaan vertaling van
 * `inbox_kind` — "Spender" voor een betalende fan, "Private chat", "Timewaster"
 * of "Shared inbox". Nieuw maakte er "Private · <chatter>" van en liet
 * "Spender" en "Timewaster" helemaal weg, terwijl juist die twee zeggen
 * waarom een regel aandacht verdient. Is de bak onbekend, dan zet oud er geen
 * badge neer.
 */
function feedBadges(r: ActivityRow): { kind: 'fc' | 'f2f' | 'ok' | 'warn' | 'bad' | 'acc' | 'mut'; text: string }[] {
  // 30-10-2026 (Jordy, opdracht C): het kanaal staat vooraan; de soort (Fan /
  // Chatter / Auto / Other) komt uit lib/feedSoort.ts en staat er direct achter.
  // Hier alleen nog de bak als die zegt waarom een regel aandacht vraagt
  // (Private chat, Spender, Timewaster; "Shared inbox" staat in de info) en de
  // bijlage, neutraal grijs zodat hij niet op een soortkleur lijkt.
  const out: { kind: 'fc' | 'f2f' | 'ok' | 'warn' | 'bad' | 'acc' | 'mut'; text: string }[] = []
  const label = BAK_LABEL[String(r.inbox_kind ?? '').toLowerCase()]
  if (label) out.push({ kind: 'mut', text: label })

  if (r.has_media || r.media_type) {
    const f2f = String(r.platform ?? '').toLowerCase() === 'f2f'
      || String(r.model_slug ?? '').startsWith('f2f_')
    const betaald = !!r.is_paid && Number(r.price ?? 0) > 0
    const wat = String(r.media_type ?? '').toLowerCase() === 'video' ? 'video' : 'photo'
    const n = Number(r.bundle_count ?? 1)
    if (n > 1) {
      // 20-10-2026 (Jordy): een bundel is één regel; de prijs geldt voor de hele bundel.
      const bedrag = betaald ? (f2f ? eur(r.price) : `${Number(r.price)} credits`) : null
      out.push({ kind: 'mut', text: bedrag ? `PPV bundle · ${n} · ${bedrag}` : `Bundle · ${n}` })
      return out
    }
    if (betaald) {
      // Zelfde eenheid als in het chatvenster: FanCentro rekent in credits.
      const bedrag = f2f ? eur(r.price) : `${Number(r.price)} credits`
      out.push({ kind: 'mut', text: `PPV ${wat} · ${bedrag}` })
    } else {
      out.push({ kind: 'mut', text: wat === 'video' ? 'Video' : 'Photo' })
    }
  }
  return out
}

/** De soortbadge: icoon + woord (+ naam of type), leesbaar ook zonder kleur. */
function SoortTag({ ind }: { ind: FeedIndeling }) {
  return (
    <span className={`tag soort-${ind.soort}`} data-testid="feed-soort" data-soort={ind.soort}>
      <span aria-hidden>{SOORT_ICOON[ind.soort]}</span>
      {ind.woord}{ind.detail ? ` · ${ind.detail}` : ''}
    </span>
  )
}

/** De legenda achter het i-knopje bij de titel Live Feed. */
const FEED_LEGENDA = (
  <span className="feedlegenda">
    <span className="tag soort-fan"><span aria-hidden>{SOORT_ICOON.fan}</span>Fan</span>
    {' '}message from the fan.
    <br />
    <span className="tag soort-chatter"><span aria-hidden>{SOORT_ICOON.chatter}</span>Chatter</span>
    {' '}sent by a team member from the dashboard, with their name.
    <br />
    <span className="tag soort-auto"><span aria-hidden>{SOORT_ICOON.auto}</span>Auto</span>
    {' '}automatic: Mass DM, Auto MM, welcome message, AI.
    <br />
    <span className="tag soort-other"><span aria-hidden>{SOORT_ICOON.other}</span>Other</span>
    {' '}source not certain (e.g. sent on the platform itself).
    <br />
    The coloured line on the left of a row has the same colour. The i on a row shows
    the rest (e.g. Shared inbox, who started a Mass DM).
  </span>
)

export function LiveFeedPanel({
  rows,
  chatters,
  chatter,
  onChatter,
  platform,
  onPlatform,
  avatarOf,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  loadingDays = false,
  onLoadDays,
}: {
  rows: ActivityRow[]
  chatters: string[]
  chatter: string
  onChatter: (v: string) => void
  platform: string
  onPlatform: (v: string) => void
  avatarOf?: (slug: unknown) => string | undefined
  /** Zijn er oudere regels op te halen? */
  hasMore?: boolean
  loadingOlder?: boolean
  onLoadOlder?: () => void
  /** Bezig met "Show 7 days". */
  loadingDays?: boolean
  /** 27-09 (Jordy): laadt in één keer door tot 7 dagen terug. */
  onLoadDays?: () => void
}) {
  const shown = useMemo(() => groepeerBundels(rows.filter((r) => {
    if (!isMessageRow(r)) return false
    if (platform !== 'all' && String(r.platform ?? '').toLowerCase() !== platform) return false
    if (chatter !== 'all' && String(r.chatter ?? '') !== chatter) return false
    return true
  })), [rows, platform, chatter])

  // Filtert de chatterkeuze het meeste weg, dan valt er niets te scrollen en
  // komt onScroll nooit. Vul dan zelf aan tot er iets te zien is.
  useEffect(() => {
    if (onLoadOlder && hasMore && !loadingOlder && rows.length > 0 && shown.length < 20) {
      onLoadOlder()
    }
  }, [onLoadOlder, hasMore, loadingOlder, rows.length, shown.length])

  return (
    <SidePanel
      tour="live-feed"
      title="Live Feed"
      desc={FEED_LEGENDA}
      onScrollEnd={hasMore && onLoadOlder ? onLoadOlder : undefined}
      right={
        <div className="row" style={{ gap: 6 }}>
          {onLoadDays ? (
            <button type="button" className="btn sm" disabled={loadingDays || !hasMore}
                    onClick={onLoadDays}>
              {loadingDays ? 'Loading 7 days…' : 'Show 7 days'}
            </button>
          ) : null}
          <select className="select sm" value={chatter} onChange={(e) => onChatter(e.target.value)}>
            <option value="all">All chatters</option>
            {chatters.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="seg plat">
            {[['all', 'All'], ['fancentro', 'FC'], ['f2f', 'F2F']].map(([v, l]) => (
              <button key={v} type="button" className={platform === v ? 'on' : undefined}
                      onClick={() => onPlatform(v)}>{l}</button>
            ))}
          </div>
        </div>
      }
    >
      {shown.length === 0 ? <EmptyRow>No activity yet.</EmptyRow> : shown.map((r, i) => {
        const ind = feedIndeling(r)
        const fc = String(r.platform ?? '').toLowerCase() !== 'f2f'
        return (
        <RijLink key={feedKey(r) || `${r.timestamp}-${i}`} id={r.conversation_id}
                 extra={`feedsoort feedsoort-${ind.soort}`}>
          <span className="mut2 ftime">{dtSec(r.timestamp)}</span>
          <Tag kind={fc ? 'fc' : 'f2f'}>{fc ? 'FC chat' : 'F2F chat'}</Tag>
          <SoortTag ind={ind} />
          {feedBadges(r).map((b, j) => <Tag key={j} kind={b.kind}>{b.text}</Tag>)}
          {ind.info.length ? (
            <InfoKnop label="Details of this message">
              {ind.info.map((t, j) => <span key={j} style={{ display: 'block' }}>{t}</span>)}
            </InfoKnop>
          ) : null}
          <span className="fmodel">
            <Avatar name={r.model} src={avatarOf?.(r.model_slug)} size={18} />
            {String(r.model ?? '—')}
          </span>
          {r.fan ? <span className="ffan">{String(r.fan)}</span> : null}
          <span className="ftext">{String(r.message ?? '')}</span>
        </RijLink>
        )
      })}
      {shown.length > 0 && loadingOlder ? <EmptyRow>Loading older…</EmptyRow> : null}
      {shown.length > 0 && !hasMore && onLoadOlder ? <EmptyRow>End of feed.</EmptyRow> : null}
    </SidePanel>
  )
}

/* ------------------------------------------------------- mass messages sent */

export interface MassRow {
  id: number
  created_at: string | null
  created_by: string | null
  platform: string
  model_slug: string | null
  model_name: string | null
  kind: string | null
  content: string | null
  status: string | null
  recipient_count: number | null
  sent_count: number | null
  failed_count: number | null
  copies_in_chats?: number | null
  has_media: boolean | null
  is_paid: boolean | null
  price: number | null
}

/** Uitklapbalk boven de tabel, precies zoals de oude pagina hem toont. */
export function MassSentBar({ rows, avatarOf }: {
  rows: MassRow[]
  avatarOf?: (slug: unknown) => string | undefined
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <button
        type="button"
        className="masshead"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ fontWeight: 600 }}>Mass messages sent — text and pictures</span>
        <span className="mut2" style={{ fontSize: 12 }}>{rows.length}</span>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="mut2">{open ? '▾' : '▸'}</span>
      </button>
      {open ? (
        <div className="body flush">
          <div className="panelscroll">
            {rows.length === 0 ? <EmptyRow>No mass messages sent.</EmptyRow> : rows.map((m) => (
              <div className="feedrow" key={m.id}>
                <Plat value={m.platform} />
                <span className="mut2 ftime">{dtShort(m.created_at)}</span>
                <span className="fmodel">
                  <Avatar name={m.model_name} src={avatarOf?.(m.model_slug)} size={18} />
                  {String(m.model_name ?? m.model_slug ?? '—')}
                </span>
                <Tag kind="acc">{String(m.created_by ?? 'Unknown')}</Tag>
                {m.has_media ? <Tag kind="warn">{m.is_paid ? 'PPV' : 'Photo'}</Tag> : null}
                <span className="ftext">{String(m.content ?? '')}</span>
                <span className="mut2">{massVoortgang(m)}</span>
                <Tag kind={String(m.status) === 'done' ? 'ok' : 'mut'}>{String(m.status ?? '—')}</Tag>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
